import React, { useState, useRef, useEffect } from "react";

import { useLocation } from "react-router-dom";

import * as Material from "@mui/material";

import SendIcon from "@mui/icons-material/Send";
import { DataGrid } from "@mui/x-data-grid";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HubIcon from "@mui/icons-material/Hub";

import { sendMessageTo } from "@contexts/ChatContext";
import { useCommunity } from "@contexts/CommunityContext";

import "dayjs/locale/pt-br";

import UsersSearch from "@components/UsersSearch";

import api from "@helpers/api.js";

import Screen from "@components/Screen";
import Pagination from "@components/Pagination";

import "./styles.css";

export default function Home() {
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [rows, setRows] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const { communities } = useCommunity();

  const params = useLocation();
  const filterByCommunities = params?.state?.filterUsers?.community_ids;

  console.log("Params", filterByCommunities);

  const REF_filters = useRef({
    search_string: "",
    community_ids: filterByCommunities
      ? [...filterByCommunities]
      : [...communities.map((c) => c.id)],
  });

  const paginationRef = useRef({
    currentPage: 1,
    total: null,
    rowsPerPage: null,
  });

  useEffect(() => {
    if (loadingUsers && !rows.length) {
      handleFilterUsers(null);
    }
  }, [loadingUsers]);

  const getRowClassName = (params) => {
    const isSelected = selectedUsers.includes(params?.id);
    const className =
      !isSelected && selectedUsers.length > 0 ? "unselected-row" : "";
    return className;
  };

  function handleRowSelected(selectedIds) {
    setSelectedUsers([...selectedIds]);
  }

  async function handleFilterUsers() {
    setLoadingUsers(true);

    console.log(REF_filters.current);

    try {
      const { data } = await api.get("users", {
        params: { filters: REF_filters.current },
      });

      paginationRef.current = {
        currentPage: data.current_page,
        total: data.total,
        rowsPerPage: data.per_page,
      };

      setRows([...data?.data]);
    } catch (e) {
      console.log(e);
    }

    setLoadingUsers(false);
  }

  return (
    <Screen title="Membros cadastrados">
      <ChallengeInfo
        total={paginationRef.current.total}
        loading={loadingUsers}
      />

      <UsersSearch onFilter={handleFilterUsers} filterRef={REF_filters} />

      <DataGrid
        color="secondary"
        disableColumnMenu={true}
        hideFooter={true}
        autoHeight
        className="users-table mt-4"
        rows={loadingUsers ? [{ id: 1 }, { id: 21 }, { id: 33 }] : rows}
        selectionModel={selectedUsers}
        columns={
          loadingUsers
            ? [
                { ...columns[0], ...FakeRowInfo },
                { ...columns[2], ...FakeRowInfo },
                { ...columns[3], ...FakeRowInfo },
              ]
            : columns
        }
        checkboxSelection
        onSelectionModelChange={handleRowSelected}
        getRowClassName={getRowClassName}
      />

      <Pagination
        paginationRef={paginationRef}
        setLoadingUsers={setLoadingUsers}
      />
    </Screen>
  );
}

const columns = [
  {
    field: "avatar",
    headerName: "",
    sortable: false,
    width: 40,
    renderCell: (params) => {
      return (
        <>
          {params.row.avatar ? (
            <img src={params.row.avatar} className="avatar" />
          ) : (
            <div className="avatar"></div>
          )}
        </>
      );
    },
  },
  {
    field: "username",
    minWidth: 200,
    headerName: <h5>Nome completo</h5>,
    sortable: false,
    renderCell: (params) => {
      return (
        <span
          className="open-user-btn"
          onClick={(e) => {
            e.stopPropagation();
            window.openUserDetails(params.row.id);
          }}
        >
          {params.row.username || params.row.email}
          <OpenInNewIcon className="open-user-icon" />
        </span>
      );
    },
    flex: 1,
  },
  {
    field: "status",
    headerAlign: "center",
    align: "center",
    headerName: <h5>Planos ativos</h5>,
    sortable: false,
    width: 220,
    renderCell: (params) => {
      const { last_payments } = params.row;

      if (!last_payments || last_payments.length === 0)
        return (
          <div className={"subscription-status no-product"}>
            <small className="active-product-name">
              Nenhum produto adquirido
            </small>
          </div>
        );

      //Retorna o produto ativo atualmente, ou o último produto com pagamento registrado
      const productSubscribed =
        last_payments.filter(({ active }) => !!active)[0] ||
        last_payments.sort((a, b) => {
          const dateA = new Date(a.renewed_until);
          const dateB = new Date(b.renewed_until);
          return dateB - dateA;
        })[0];

      const subscribedUntilDays = daysUntilDate(
        productSubscribed.renewed_until
      );

      return (
        <div
          className={
            "subscription-status " +
            (subscribedUntilDays > 7
              ? "active"
              : subscribedUntilDays > 0
              ? "alert"
              : "pending")
          }
        >
          <div className="current-subscription">
            <span className={"subscription-status-marker "}></span>
            <small className="active-product-name">
              {productSubscribed.product_name}
            </small>
          </div>
          <div className="current-subscription">
            <small className="valid-until">
              {subscribedUntilDays > 0 ? "Ativo até:" : "Venceu em:"}{" "}
              {prepareDate(productSubscribed.renewed_until)}
            </small>
            <small className="valid-until-badge">
              {subscribedUntilDays > 0 ? "+" : "há "}
              {Math.abs(subscribedUntilDays)} dias
            </small>
          </div>
        </div>
      );

      function prepareDate(date) {
        const ymd = `${date}`?.split(" ")[0];
        return ymd?.split("-").reverse().join("/") || "-";
      }
      function daysUntilDate(data) {
        const dataAlvo = new Date(data);
        const dataAtual = new Date();
        dataAtual.setHours(0, 0, 0, 0);
        dataAlvo.setHours(0, 0, 0, 0);
        const diferencaEmMilissegundos = dataAlvo - dataAtual;
        const diferencaEmDias = Math.ceil(
          diferencaEmMilissegundos / (1000 * 60 * 60 * 24)
        );
        return diferencaEmDias;
      }
    },
  },
  {
    field: "actions",
    headerName: "",
    headerAlign: "center",
    width: 80,
    align: "center",
    sortable: false,
    renderCell: (params) => (
      <div className="table-actions" style={{ gap: "1px" }}>
        <Material.Tooltip title="Enviar mensagem" arrow>
          <Material.IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              sendMessageTo(params.row);
            }}
            children={<SendIcon />}
          />
        </Material.Tooltip>
      </div>
    ),
  },
];

const FakeRowInfo = {
  renderCell: () => (
    <Material.Skeleton variant="rectangular" width={400} height={30} />
  ),
};

const ChallengeInfo = ({ total, loading }) => {
  return (
    <div className="challenge-info-bar mb-5">
      <div className="challenge-info main-info p-2 mt-3">
        <HubIcon
          color="white"
          height={80}
          width={80}
          className="main-info-icon"
        />

        <div>
          <h5>Total de membros</h5>
          {loading ? "-" : <h2>{total || 0}</h2>}
        </div>
      </div>
    </div>
  );
};
